import { Trans, useTranslation } from 'react-i18next'

import { Alert, IconArrowUpRight, Text } from '@vtex/shoreline'

import { ButtonCopyToClipboard } from '~/components/Buttons'

import { MOBILE_SDK_DOCS_URL, WEB_DOCS_URL, buildWebSnippet } from '../helpers'
import {
  Card,
  CardHeading,
  CardTitle,
  CodeBlock,
  DocButton,
  InlineCode,
  InstallPath,
  InstallPaths,
  InstallStack,
  ScriptRow,
  SectionRow,
  StatusBadge,
  StatusDot,
  SubLabel,
  SubText
} from '../styles'
import type { ScriptStatus } from '../types'

const SurfaceStatus = ({ status }: { status?: ScriptStatus }) => {
  const { t } = useTranslation()
  const active = !!status?.active
  const label = active
    ? t('rm:config.destinationRetail.installation.status.detected')
    : t('rm:config.destinationRetail.installation.status.notDetected')

  return (
    <StatusBadge $active={active}>
      <StatusDot $active={active} />
      {label}
    </StatusBadge>
  )
}

const Installation = ({
  scriptStatus,
  error
}: {
  scriptStatus?: {
    web: ScriptStatus
    mobile: ScriptStatus
  }
  error: boolean
}) => {
  const { t } = useTranslation()
  const webSnippet = buildWebSnippet()

  return (
    <Card>
      <CardHeading>
        <CardTitle>
          {t('rm:config.destinationRetail.installation.title')}
        </CardTitle>
        <Text variant="body">
          {t('rm:config.destinationRetail.installation.description')}
        </Text>
      </CardHeading>

      {error ? (
        <Alert variant="critical">
          {t('common:messages.errorOccurred.message')}
        </Alert>
      ) : (
        <InstallStack>
          <section>
            <SectionRow>
              <Text variant="action">
                {t('rm:config.destinationRetail.installation.web')}
              </Text>
              <SurfaceStatus status={scriptStatus?.web} />
            </SectionRow>

            <InstallPaths>
              <InstallPath>
                <SubLabel>
                  {t('rm:config.destinationRetail.installation.vtex.label')}
                </SubLabel>
                <SubText>
                  <Trans
                    i18nKey="rm:config.destinationRetail.installation.vtex.instructions"
                    components={{ code: <InlineCode /> }}
                  />
                </SubText>
              </InstallPath>

              <InstallPath>
                <SubLabel>
                  {t(
                    'rm:config.destinationRetail.installation.storefront.label'
                  )}
                </SubLabel>
                <SubText>
                  {t(
                    'rm:config.destinationRetail.installation.storefront.instructions'
                  )}
                </SubText>
                <ScriptRow>
                  <CodeBlock>
                    <code>{webSnippet}</code>
                  </CodeBlock>
                  <ButtonCopyToClipboard
                    template="secondary"
                    toCopy={webSnippet}
                    textBefore={t(
                      'rm:config.destinationRetail.installation.copy'
                    )}
                    textAfter={t(
                      'rm:config.destinationRetail.installation.copied'
                    )}
                  />
                </ScriptRow>
              </InstallPath>
            </InstallPaths>

            <DocButton
              href={WEB_DOCS_URL}
              target="_blank"
              rel="noreferrer noopener"
              aria-label={t(
                'rm:config.destinationRetail.installation.docs.generalAria'
              )}
              style={{ marginTop: 'var(--sl-space-5, 1.25rem)' }}
            >
              {t('rm:config.destinationRetail.installation.docs.general')}
              <IconArrowUpRight />
            </DocButton>
          </section>

          <section>
            <SectionRow>
              <Text variant="action">
                {t('rm:config.destinationRetail.installation.mobile')}
              </Text>
              <SurfaceStatus status={scriptStatus?.mobile} />
            </SectionRow>

            <InstallPaths>
              <InstallPath>
                <SubText>
                  {t(
                    'rm:config.destinationRetail.installation.mobile.instructions'
                  )}
                </SubText>
                <DocButton
                  href={MOBILE_SDK_DOCS_URL}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label={t(
                    'rm:config.destinationRetail.installation.docs.mobileAria'
                  )}
                  style={{ marginTop: 'var(--sl-space-3, 0.75rem)' }}
                >
                  {t('rm:config.destinationRetail.installation.docs.mobile')}
                  <IconArrowUpRight />
                </DocButton>
              </InstallPath>
            </InstallPaths>
          </section>
        </InstallStack>
      )}
    </Card>
  )
}

export default Installation
